import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/auth.store';
import { UserAccessService } from '@/features/user-access/user-access.service';
import type { IBranch } from '@/features/user-access/user-access.types';

/**
 * The company's active branches — one query, shared by every screen that needs
 * the list.
 *
 * There used to be four callers of `/branches`, each with its own filter
 * object: the header's location popover (`limit: 1000`, unsorted, no
 * `companyId`), the booking pickers (`limit: 100`, sorted by name), the
 * inventory location chips (`limit: 100`, unsorted) and `useDefaultLocation`
 * (`limit: 100`, sort/order/search left `undefined`). Same endpoint, same
 * rows, four shapes — and a TanStack query key IS the request, so no two of
 * them ever shared a cache entry. Opening the planner fetched the identical
 * 11 kB twice in one render, once for the popover and once to resolve the
 * default branch.
 *
 * A page of 1000 rather than 100, so no consumer silently truncates a company
 * that outgrows one page; sorted by name, because every one of these lists is
 * rendered for a human to pick from.
 */
const BRANCH_LIST_FILTERS = {
  search: '',
  page: 1,
  limit: 1000,
  sort: 'name',
  order: 'asc',
  status: 'active',
} as const;

type BranchListOptions = {
  /**
   * Whether the query runs. Defaults to true; pass the condition under which
   * the caller actually renders the list, so a screen that never shows it does
   * not pay for it. Other callers on the same page are unaffected — one
   * enabled consumer fetches for all of them.
   */
  enabled?: boolean;
  /**
   * Another company's branches instead of the signed-in one's: the super
   * Admin picking where a "View as" role works.
   */
  companyId?: string;
};

const useBranchList = ({
  enabled = true,
  companyId: otherCompanyId,
}: BranchListOptions = {}) => {
  const ownCompanyId = useAuthStore(state => state.user?.company?.id ?? '');
  const companyId = otherCompanyId ?? ownCompanyId;

  const { data, isLoading } = useQuery({
    queryKey: ['locations', { ...BRANCH_LIST_FILTERS, companyId }],
    queryFn: UserAccessService.getListLocation,
    enabled,
    select: data => data?.data,
  });

  // Memoised: callers pass this into effect dependency arrays, and a fresh
  // `[]` on every render would re-run them forever.
  const branches: IBranch[] = useMemo(() => data ?? [], [data]);

  return {
    branches,
    isLoading,
    /**
     * Whether a response has landed. `branches` cannot say so on its own: an
     * empty array is both "not fetched yet" and "this company has no active
     * branch", and a caller that seeds a default from the list has to tell
     * those apart.
     */
    isLoaded: data !== undefined,
  };
};

export { useBranchList, BRANCH_LIST_FILTERS };
